import { useState, useEffect, useCallback, useRef } from "react";
import { DropdownEnum } from "enum/DropdownEnum";
import {
  getWhsDropdownOptions,
  addWhsDropdownOption,
  editWhsDropdownOption,
  deleteWhsDropdownOption,
} from "services/whs/whsService";
import { getJobUsers } from "services/jobsService";
import { getInternalUsersForAssigningLeads, getAllUsers } from "services/userService";
import { toast } from "sonner";

export function useToolboxTalkLookups({
  companyId,
  userId,
  jobData,
  locationState,
  loggedInUser,
  presenter,
  setPresenter,
  status,
  setStatus,
}) {
  const [users, setUsers] = useState([]);
  const [internalUsers, setInternalUsers] = useState([]);
  const [loadingJobUsers, setLoadingJobUsers] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const statusInitializedRef = useRef(false);

  // 1. Fetch ToolboxTalkStatus options via /api/WHS/DropdownOptions?companyId={companyId}&enumValue=1004
  const refreshStatusOptions = useCallback(async () => {
    if (!companyId) return;
    setLoadingStatus(true);
    try {
      const res = await getWhsDropdownOptions(companyId, DropdownEnum.ToolboxTalkStatus);
      if (res?.success) {
        const mapped = (res.data || []).map((opt) => ({
          value: String(opt.id || opt.Id).toLowerCase(),
          label: opt.optionName || opt.OptionName,
        }));
        setStatusOptions(mapped);

        // Default to "Draft" option if no status is set and options are loaded
        if (!statusInitializedRef.current && mapped.length > 0) {
          statusInitializedRef.current = true;
          const draftOpt = mapped.find(
            (opt) => opt.label.toLowerCase() === "draft"
          );
          if (draftOpt) {
            setStatus(draftOpt.value);
          } else {
            setStatus(mapped[0].value);
          }
        }
      }
    } catch (err) {
      console.error("Failed to load WHS toolbox talk statuses:", err);
    } finally {
      setLoadingStatus(false);
    }
  }, [companyId, setStatus]);

  useEffect(() => {
    refreshStatusOptions();
  }, [refreshStatusOptions]);

  // Helper to resolve an effective User ID for adding options
  const getEffectiveUserId = useCallback(() => {
    if (userId) return userId;
    const fromState = locationState?.userId || locationState?.userForm?.userId;
    if (fromState) return fromState;
    const fromStorage = localStorage.getItem("whs_user_id");
    if (fromStorage) return fromStorage;
    const fromAuth = loggedInUser?.id || loggedInUser?.userId;
    if (fromAuth) return fromAuth;
    if (presenter) return presenter;
    return null;
  }, [userId, locationState, loggedInUser, presenter]);

  // Status CRUD Handlers
  const handleCreateStatus = useCallback(
    async (name) => {
      if (!name?.trim()) return { success: false };
      if (!companyId) {
        toast.error("Company context is missing.");
        return { success: false };
      }
      const effectiveUserId = getEffectiveUserId();
      if (!effectiveUserId) {
        toast.error("User context is required to add an option.");
        return { success: false };
      }

      try {
        const res = await addWhsDropdownOption({
          companyId,
          userId: effectiveUserId,
          enumValue: DropdownEnum.ToolboxTalkStatus,
          optionName: name.trim(),
        });

        if (res?.success && res?.optionId) {
          toast.success("Status added successfully");
          const newOpt = { value: String(res.optionId).toLowerCase(), label: name.trim() };
          setStatusOptions((prev) => [...prev, newOpt]);
          setStatus(String(res.optionId).toLowerCase());
          refreshStatusOptions();
          return { success: true, optionId: res.optionId };
        } else {
          toast.error(res?.error || res?.message || "Failed to add status");
          return { success: false };
        }
      } catch (err) {
        console.error("Error adding status:", err);
        toast.error("Failed to add status");
        return { success: false };
      }
    },
    [companyId, getEffectiveUserId, refreshStatusOptions, setStatus]
  );

  const handleEditStatus = useCallback(
    async (id, currentLabel, newText) => {
      if (!newText?.trim() || newText.trim() === currentLabel?.trim()) return false;
      if (!companyId) {
        toast.error("Company context is missing.");
        return false;
      }

      try {
        const res = await editWhsDropdownOption({
          optionId: id,
          companyId,
          optionName: newText.trim(),
        });

        if (res?.success) {
          toast.success("Status updated successfully");
          setStatusOptions((prev) =>
            prev.map((opt) =>
              String(opt.value).toLowerCase() === String(id).toLowerCase()
                ? { ...opt, label: newText.trim() }
                : opt
            )
          );
          refreshStatusOptions();
          return true;
        } else {
          toast.error(res?.error || res?.message || "Failed to update status");
          return false;
        }
      } catch (err) {
        console.error("Error editing status:", err);
        toast.error("Failed to update status");
        return false;
      }
    },
    [companyId, refreshStatusOptions]
  );

  const handleDeleteStatus = useCallback(
    async (id) => {
      if (!companyId) {
        toast.error("Company context is missing.");
        return false;
      }

      try {
        const res = await deleteWhsDropdownOption({
          optionId: id,
          companyId,
        });

        if (res?.success) {
          toast.success("Status deleted successfully");
          setStatusOptions((prev) =>
            prev.filter((opt) => String(opt.value).toLowerCase() !== String(id).toLowerCase())
          );
          if (String(status)?.toLowerCase() === String(id)?.toLowerCase()) {
            setStatus("");
          }
          refreshStatusOptions();
          return true;
        } else {
          toast.error(res?.error || res?.message || "Failed to delete status");
          return false;
        }
      } catch (err) {
        console.error("Error deleting status:", err);
        toast.error("Failed to delete status");
        return false;
      }
    },
    [companyId, status, setStatus, refreshStatusOptions]
  );

  // 2. Load Internal Users for "Conducted By"
  useEffect(() => {
    let isMounted = true;
    const fetchInternalUsers = async () => {
      try {
        const res = await getInternalUsersForAssigningLeads(companyId || undefined);
        if (res?.success && res.data && isMounted) {
          const raw = Array.isArray(res.data)
            ? res.data
            : typeof res.data === "string"
            ? JSON.parse(res.data)
            : [];

          const parsedUsers = raw.map((u) => ({
            ...u,
            value: String(u.value || u.userId || u.id || u.UserId || "").toLowerCase(),
            label: u.label || u.fullName || u.FullName || u.name || "Unnamed User",
            designation: u.designationName || u.designations || u.DesignationName || "",
          }));

          setInternalUsers(parsedUsers);

          // Auto-select active user as presenter if matched
          const activeUserId =
            userId ||
            locationState?.userId ||
            locationState?.userForm?.userId ||
            localStorage.getItem("whs_user_id") ||
            loggedInUser?.id ||
            loggedInUser?.userId ||
            "";

          if (activeUserId && !presenter) {
            const matched = parsedUsers.find(
              (u) => String(u.value).toLowerCase() === String(activeUserId).toLowerCase()
            );
            if (matched && isMounted) {
              setPresenter(matched.value);
            }
          }
        }
      } catch (err) {
        console.error("Failed to load internal users for conducted by:", err);
      }
    };

    fetchInternalUsers();
    return () => {
      isMounted = false;
    };
  }, [companyId, userId, loggedInUser, locationState, presenter, setPresenter]);

  // 3. Load Job Users / Attendees with companyId & graceful fallback
  useEffect(() => {
    const activeJobId = jobData?.jobId || jobData?.JobId;
    if (!activeJobId && !companyId) return;

    let isMounted = true;
    const fetchJobWorkers = async () => {
      setLoadingJobUsers(true);
      try {
        let rawUsers = [];

        // First attempt: getJobUsers passing activeJobId and companyId
        if (activeJobId) {
          try {
            const res = await getJobUsers(activeJobId, companyId || undefined);
            if (res?.success && Array.isArray(res.data) && res.data.length > 0) {
              rawUsers = res.data;
            }
          } catch (jobUsersErr) {
            console.warn("getJobUsers call failed, falling back to company users list:", jobUsersErr);
          }
        }

        // Fallback: getAllUsers passing companyId
        if (rawUsers.length === 0 && companyId) {
          try {
            const allRes = await getAllUsers({ companyId, pageSize: 500 });
            if (allRes?.data && Array.isArray(allRes.data) && allRes.data.length > 0) {
              rawUsers = allRes.data;
            }
          } catch (allUsersErr) {
            console.warn("getAllUsers fallback failed:", allUsersErr);
          }
        }

        if (isMounted) {
          const parsedUsers = (rawUsers || []).map((u) => {
            const uid = String(u.UserId || u.userId || u.Id || u.id || u.value || "").toLowerCase();
            const uname = u.FullName || u.fullName || u.Name || u.name || u.label || "Unnamed User";
            return {
              ...u,
              value: uid,
              label: uname,
              userId: uid,
              id: uid,
              designation:
                u.Designations ||
                u.designations ||
                u.Designation ||
                u.designation ||
                u.DesignationName ||
                u.designationName ||
                u.roleName ||
                u.RoleName ||
                "",
            };
          }).filter((u) => Boolean(u.value));

          setUsers(parsedUsers);
        }
      } catch (err) {
        console.error("Failed to load attendees for toolbox talk:", err);
      } finally {
        if (isMounted) setLoadingJobUsers(false);
      }
    };

    fetchJobWorkers();
    return () => {
      isMounted = false;
    };
  }, [jobData, companyId]);

  return {
    users,
    internalUsers,
    loadingJobUsers,
    statusOptions,
    loadingStatus,
    handleCreateStatus,
    handleEditStatus,
    handleDeleteStatus,
  };
}

export default useToolboxTalkLookups;
